import {
  Children,
  cloneElement,
  isValidElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import { LayoutProps, layout, SpaceProps, space } from "styled-system";

import { useScrollPosition } from "../useScrollPosition";
import TableHead from "../TableHead/TableHead";
import TableBody from "../TableBody/TableBody";
import TableRow from "../TableRow/TableRow";

interface TableProps extends LayoutProps, SpaceProps {
  /**
   * children elements
   */
  children: ReactNode;
  /**
   *The element's unique identifier
   */
  id?: string;
  /**
   * each row will be numbered (adds an extra column at the beginning)
   */
  autoNumbering?: boolean;
  /**
   * the height of the overlay that is displayed when the table is scrollable
   */
  overlayHeight?: number;
  /**
   * makes the header sticky
   */
  stickyHeader?: boolean;
  /**
   border-collapse css property applied on table element
   */
  borderCollapse?: BorderCollapse;
  /**
   Allows the user to specify any custom styles.
  */
  css?: string;
}

type BorderCollapse = "separate" | "collapse" | "initial" | "inherit";

const StyledTable = styled.table<
  LayoutProps &
    SpaceProps & { autoNumbering: boolean; borderCollapse: BorderCollapse }
>`
  position: relative;
  text-align: left;
  border-spacing: 0px;
  counter-reset: rowNumber;
  height: auto;
  ${(props) =>
    css({
      fontFamily: "normal",
      fontSize: "body",
      fontWeight: "normal",
      borderCollapse: props.borderCollapse,
    })};
`;

const OverlayContainer = styled.div<
  {
    scrollbarWidth: number;
    scrollbarHeight: number;
    headerHeight?: number | undefined;
    overlayHeight: number;
    css?: string;
  } & LayoutProps
>`
  box-sizing: border-box;
  ${css({
    border: "1px solid",
    borderColor: "table-border-color",
    borderRadius: 2,
  })}
  overflow: hidden;
  position: relative;
  ${(props) => `&.bottom:after {
    box-sizing: border-box;
    position: absolute;
    left: 0;
    bottom: ${props.scrollbarHeight}px;
    width: calc(100% - ${props.scrollbarWidth}px);
    height: ${props.overlayHeight || 0}px;
    background: linear-gradient(0deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);

    content: "";
  }

  &.top:before {
    box-sizing: border-box;
    position: absolute;
    left: 0;
    top: ${props.headerHeight}px;
    width: calc(100% - ${props.scrollbarWidth}px);
    height: 86px;
    background: linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
    content: "";
    z-index: 1;
  }`}
  ${layout}
  ${space}
  ${(props) => props.css}
`;

const TableContainer = styled.div<LayoutProps>`
  height: 100%;
  overflow: auto;
  ${layout}
`;

const Table = ({
  children,
  id,
  maxWidth = 1024,
  width = "100%",
  height = "auto",
  maxHeight,
  overlayHeight = 68,
  autoNumbering = false,
  stickyHeader = false,
  borderCollapse = "separate",
  ...props
}: TableProps) => {
  const [displayOverlay, setDisplayOverlay] = useState<boolean>(false);
  const [overlayClasses, setOverlayClasses] = useState<string>();
  const [scrollbarSizes, setScrollbarSizes] = useState({
    vertical: 0,
    horizontal: 0,
  });
  const [tableContainerRef, scrollPosition] = useScrollPosition();
  const overlayRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (scrollPosition === "MIDWAY") {
      setOverlayClasses("top bottom");
    }
    if (scrollPosition === "BOTTOM") {
      setOverlayClasses("top");
    }
    if (scrollPosition === "TOP") {
      setOverlayClasses("bottom");
    }
  }, [scrollPosition]);

  useEffect(() => {
    const container = tableContainerRef.current;

    if (container) {
      setScrollbarSizes({
        vertical: container.offsetWidth - container.clientWidth,
        horizontal: container.offsetHeight - container.clientHeight,
      });

      if (container.scrollHeight > container.offsetHeight) {
        setDisplayOverlay(true);
      }
    }
  }, [tableContainerRef]);

  const renderChildren = () => {
    return Children.toArray(children).map((child) => {
      if (isValidElement(child)) {
        const isHeader = child.type === TableHead;
        const isBody = child.type === TableBody;

        if (isHeader || isBody) {
          const newChildren = Children.map(child.props.children, (c, index) => {
            if (c.type === TableRow) {
              return cloneElement(c, {
                ...(autoNumbering && { autoNumbering: autoNumbering }),
                ...(autoNumbering && isBody && { rowNumber: index + 1 }),
              });
            }
            return c;
          });

          return cloneElement(child, {
            ...(isHeader && { ref: headerRef }),
            ...(isHeader && stickyHeader && { sticky: stickyHeader }),
            children: newChildren,
          });
        }
      }

      return child;
    });
  };

  return (
    <OverlayContainer
      className={displayOverlay ? overlayClasses : ""}
      maxWidth={maxWidth}
      width={width}
      scrollbarWidth={scrollbarSizes.vertical}
      scrollbarHeight={scrollbarSizes.horizontal}
      headerHeight={stickyHeader ? headerRef?.current?.clientHeight : 0}
      overlayHeight={overlayHeight}
      ref={overlayRef}
      {...props}
    >
      <TableContainer
        ref={tableContainerRef}
        maxHeight={maxHeight}
        width="100%"
        height={height}
      >
        <StyledTable
          id={id}
          tabIndex={0}
          width="100%"
          autoNumbering={autoNumbering}
          borderCollapse={borderCollapse}
        >
          {renderChildren()}
        </StyledTable>
      </TableContainer>
    </OverlayContainer>
  );
};

export default Table;
